import React, { Component } from "react";
import PropTypes from "prop-types";
import { ApplicantService } from "@services";
import { UN_ASSIGNMENT_REASONS, DAYS_OF_WEEK, RECURRENCE_FREQUENCIES } from "@constants";
import utils from "@utils";
import Loader from "../Loader/Loader";
import Modal from "../Modal/Modal";

import "./UnassignOfficerModal.scss";

class UnassignOfficerModal extends Component {
	constructor(props) {
		super(props);

		this.initialState = {
			loading: false,
			recurrenceType: "one",
			reasonType: "",
			reasonDetails: "",
			selectedDays: [],
			errors: {
				reasonType: false,
				reasonDetails: false,
			},
		};

		this.state = { ...this.initialState };
	}

	handleChange = event => {
		const {
			target: { name, value },
		} = event;
		this.setState({ [name]: value });
	};

	/**
	 * Handles all changes made when selectng a day of the week
	 * @param {String} day The selected day
	 */
	handleDayChange = day => {
		let { selectedDays } = this.state;
		if (selectedDays.includes(day)) selectedDays = selectedDays.filter(item => item !== day);
		else selectedDays.push(day);
		this.setState({ selectedDays });
	};

	/** Validates all inputs related to unaasiging an officer */
	validateForm = () => {
		const { recurrenceType, reasonType, reasonDetails, selectedDays, errors } = this.state;

		if (recurrenceType === "days" && selectedDays.length === 0) {
			utils.notify({
				type: "error",
				message: "At least one day must be selected",
				timeOut: 2000,
			});
			return false;
		}

		errors.reasonType = reasonType === "";
		errors.reasonDetails = reasonDetails === "";
		this.setState({ errors });

		return Object.values(errors).filter(err => err === true).length === 0;
	};

	/** Gathers and validates all required information necessary for unassigning an officer from a job */
	unassignOfficer = () => {
		const { lang } = global;
		const { recurrenceType, reasonType, reasonDetails, selectedDays } = this.state;
		const {
			positionId,
			unassignProps: { officerId, recurringAssignment },
			onClose,
		} = this.props;
		if (this.validateForm()) {
			let updateAllUpcoming;
			if (recurringAssignment) {
				updateAllUpcoming = recurrenceType !== "one";
			}
			const body = {
				UnassignmentRepeatsOn: selectedDays.join(),
				jobId: positionId,
				userId: officerId,
				unassignmentReasonType: reasonType,
				unassignmentReasonDetails: reasonDetails,
			};
			this.setState({ loading: true });
			ApplicantService.unAssignOfficer(positionId, { updateAllUpcoming }, body)
				.then(() => {
					utils.notify({
						type: "success",
						message: lang.t("Officer unassigned successfully"),
						title: "Success",
					});
					onClose(true);
				})
				.catch((error) =>
					utils.notify({
						type: "error",
						message: error.response?.data?.Message || lang.t("Error unassigning Officer"),
						timeOut: 2000,
					}),
				)
				.finally(() => this.setState({ loading: false }));
		}
	};

	/**
	 * For recurring jobs only
	 * Renders a list of days which an officer can be unassigned from
	 * @returns {HTMLElement} UI which will render a list of week days
	 */
	renderAvailableDays = () => {
		const { selectedDays } = this.state;
		const {
			unassignProps: { recurrenceRule },
		} = this.props;

		let availableDays = DAYS_OF_WEEK;

		if (recurrenceRule) {
			if (recurrenceRule.RepeatType === RECURRENCE_FREQUENCIES.WEEKDAYS.value) {
				availableDays = DAYS_OF_WEEK.filter(item => item.frequencyType === "weekday");
			} else if (recurrenceRule.RepeatType === RECURRENCE_FREQUENCIES.WEEKENDS.value) {
				availableDays = DAYS_OF_WEEK.filter(item => item.frequencyType === "weekend");
			} else if (
				recurrenceRule.RepeatType === RECURRENCE_FREQUENCIES.WEEKLY.value &&
				recurrenceRule.RepeatsOn
			) {
				availableDays = recurrenceRule.RepeatsOn.split(",").map(item =>
					DAYS_OF_WEEK.find(day => item.toLowerCase() === day.shortName),
				);
			}
		}

		return (
			<div className="row form-group">
				<div className="col-xs-12">
					<For
						each="day"
						of={availableDays}
					>
						<div
							key={`unassign-day-${day.shortName}`}
							className="DOW-container"
						>
							<input
								type="checkbox"
								name={`DOW_${day.shortName}`}
								id={`DOW_${day.shortName}`}
								checked={selectedDays.indexOf(day.shortName) !== -1}
								onChange={() => this.handleDayChange(day.shortName)}
							/>
							<label
								htmlFor={`DOW_${day.shortName}`}
								title={day.label}
							>
								{day.label.charAt(0)}
							</label>
						</div>
					</For>
				</div>
			</div>
		);
	};

	render() {
		const { lang } = global;
		const { loading, recurrenceType, reasonType, reasonDetails, errors } = this.state;
		const {
			unassignProps: { show, officerName, recurringAssignment },
			onClose,
		} = this.props;

		return (
			<Modal
				show={show}
				onExited={() => this.setState({ ...this.initialState })}
				id="unassign-officer-modal"
			>
				<Modal.Header>
					<Modal.Title>
						{lang.t("dynamic.Unassigning officer", { officerName })}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<If condition={recurringAssignment}>
						<div className="row form-group">
							<div className="col-sm-12">
								<div>
									{`${lang.t("This job is part of a recurring series_")}
									${lang.t("Do you want to unassign only this instance, or all upcoming instances of this series_")}`}
								</div>
								<div className="radio">
									<label htmlFor="recursive_one">
										<input
											type="radio"
											name="recursiveType"
											id="recursive_one"
											onChange={() =>
												this.setState({ recurrenceType: "one" })
											}
											checked={recurrenceType === "one"}
										/>
										{lang.t("Only this job")}
									</label>
								</div>
								<div className="radio">
									<label htmlFor="recursive_all">
										<input
											type="radio"
											name="recursiveType"
											id="recursive_all"
											onChange={() =>
												this.setState({ recurrenceType: "all" })
											}
											checked={recurrenceType === "all"}
										/>
										{lang.t("All upcoming jobs in this series")}
									</label>
								</div>
								<div className="radio">
									<label htmlFor="recursive_days">
										<input
											type="radio"
											name="recursiveType"
											id="recursive_days"
											onChange={() =>
												this.setState({ recurrenceType: "days" })
											}
											checked={recurrenceType === "days"}
										/>
										{lang.t(
											"Unassign this officer to the following days of the week in this series",
										)}
										:
									</label>
								</div>
							</div>
						</div>
						<If condition={recurrenceType === "days"}>{this.renderAvailableDays()}</If>
					</If>
					<div className="row form-group">
						<div className="col-sm-12">
							<label htmlFor="unassign-reason">
								{`${lang.t("Why are you un-assigning")} ${officerName}?`}
							</label>
						</div>
						<div className="col-sm-12">
							<small>{`${lang.t("(*)These options count towards call-offs")}`}</small>
						</div>
						<div className="col-sm-12">
							<select
								className={`form-control${errors.reasonType ? " has-error" : ""}`}
								name="reasonType"
								id="unassign-reason"
								value={reasonType}
								onChange={this.handleChange}
							>
								<option value="">{lang.t("Select Reason")}</option>
								<For
									each="item"
									of={UN_ASSIGNMENT_REASONS}
								>
									<option
										key={`unassign-reason-${item.value}`}
										value={item.value}
									>
										{item.label}
									</option>
								</For>
							</select>
						</div>
					</div>
					<div className="row form-group">
						<div className="col-sm-12">
							<label htmlFor="reason-details">
								{lang.t("Unassignment Reason Details")}
							</label>
						</div>
						<div className="col-sm-12">
							<textarea
								className={`form-control${errors.reasonDetails ? " has-error" : ""}`}
								name="reasonDetails"
								id="reason-details"
								maxLength="50"
								rows={3}
								value={reasonDetails}
								onChange={this.handleChange}
							/>
						</div>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						className="btn btn-link"
						onClick={onClose}
					>
						{lang.t("Back")}
					</button>
					<button
						type="button"
						className="btn btn-danger"
						onClick={this.unassignOfficer}
					>
						{lang.t("Unassign")}
					</button>
				</Modal.Footer>
				<Loader active={loading} />
			</Modal>
		);
	}
}

UnassignOfficerModal.propTypes = {
	positionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
	unassignProps: PropTypes.shape({
		show: PropTypes.bool,
		officerId: PropTypes.number,
		officerName: PropTypes.string,
		recurringAssignment: PropTypes.bool,
		recurrenceRule: PropTypes.object,
	}),
	onClose: PropTypes.func,
};

UnassignOfficerModal.defaultProps = {
	unassignProps: {
		show: false,
		officerId: undefined,
		officerName: undefined,
		recurringAssignment: false,
		recurrenceRule: undefined,
	},
	onClose: () => {},
};

export default UnassignOfficerModal;
